import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { OAuthResourceServerErrorHandler, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private static readonly logoutInProgressStorageKey = 'lms.logout.in.progress';
  private isRedirecting = false; // Prevent multiple simultaneous redirects
  private isLoggingOut = false; // Flag to prevent redirect attempts during logout
  private logoutGuardTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private auth: OAuthService, private authStorage: OAuthStorage) {}

  getToken(): string | null {
    let token = this.authStorage.getItem('access_token');
    return token;
    // const token = this.auth.getAccessToken();
    // if (token && token !== localStorage.getItem('access_token')) {
    //   localStorage.setItem('access_token', token);
    // }
    // return token || localStorage.getItem('access_token');
  }

  hasValidToken(): boolean {
    return this.auth.hasValidAccessToken() && !!localStorage.getItem('access_token');
  }

  get logoutInProgress(): boolean {
    return this.isLoggingOut || localStorage.getItem(TokenService.logoutInProgressStorageKey) === 'true';
  }

  async ensureTokenOrRedirect() {
    // Skip redirect during logout process
    if (this.isLoggingOut) {
      console.log('🚪 Logout in progress, skipping token redirect');
      return;
    }

    // If we have a valid token and auth redirect flag, just load profile and clear flag
    if(this.auth.hasValidAccessToken() && localStorage.getItem('auth_redirected')) {
      console.log('✅ Valid token with redirect flag - clearing flag');
      localStorage.removeItem('auth_redirected');
      try {
        await this.auth.loadUserProfile();
      } catch (err) {
        console.error('Failed to load user profile:', err);
      }
      return;
    }

    // Prevent multiple simultaneous redirects
    if (this.isRedirecting) {
      console.log('🔄 Already redirecting, skipping duplicate redirect');
      return;
    }

    // Only redirect if we don't have a valid token AND haven't already redirected
    if (!this.hasValidToken() && !localStorage.getItem('auth_redirected')) {
      console.warn('🔐 No valid token - initiating auth flow');
      this.isRedirecting = true;
      localStorage.setItem('auth_redirected', 'true');
      this.auth.initCodeFlow();
    }
  }

  /**
   * Mark logout in progress to prevent token redirect attempts
   */
  markLogoutInProgress(): void {
    this.isLoggingOut = true;
    localStorage.setItem(TokenService.logoutInProgressStorageKey, 'true');

    // Fail-safe: never keep logout guard forever, or login redirects can get stuck.
    if (this.logoutGuardTimeoutId) {
      clearTimeout(this.logoutGuardTimeoutId);
    }
    this.logoutGuardTimeoutId = setTimeout(() => {
      this.isLoggingOut = false;
      localStorage.removeItem(TokenService.logoutInProgressStorageKey);
      this.logoutGuardTimeoutId = null;
    }, 1500);
  }

  /**
   * Reset logout flag to allow fresh authentication attempts
   */
  resetLogoutFlag(): void {
    console.log('🔄 Resetting logout flag for fresh auth');
    this.isLoggingOut = false;
    localStorage.removeItem(TokenService.logoutInProgressStorageKey);
    if (this.logoutGuardTimeoutId) {
      clearTimeout(this.logoutGuardTimeoutId);
      this.logoutGuardTimeoutId = null;
    }
  }

  /**
   * Clear authentication state on logout
   * This allows the next user to login without requiring a page refresh
   * Clears both custom app state and angular-oauth2-oidc library state
   */
  clearAuthState(): void {
    console.log('🧹 Clearing auth state for logout');

    // Clear custom app state
    localStorage.removeItem('auth_redirected');
    localStorage.removeItem('auth_logout');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('granted_scopes');

    // Clear angular-oauth2-oidc library state/nonce to prevent mismatches on re-login
    // These keys are set by the oauth library when using setStorage(localStorage)
    localStorage.removeItem('state');
    localStorage.removeItem('nonce');
    localStorage.removeItem('code_verifier');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('id_token');
    localStorage.removeItem('id_token_claims');
    localStorage.removeItem('id_token_expires_at');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('session_state');
    localStorage.removeItem('PKCE_verifier');

    // Also clear sessionStorage in case any OAuth state is stored there
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('id_token_claims');
    sessionStorage.removeItem('state');
    sessionStorage.removeItem('nonce');
    sessionStorage.removeItem('code_verifier');
    sessionStorage.removeItem('session_state');
    sessionStorage.removeItem('granted_scopes');

    // Clear OAuth login retry counter to allow fresh login attempts
    sessionStorage.removeItem('oauth_login_retry_count');

    this.isRedirecting = false;
    // Keep isLoggingOut = true to block any pending redirects during page navigation
  }
}
export class OAuthNoopResourceServerErrorHandler implements OAuthResourceServerErrorHandler {

    handleError(err: HttpResponse<any>): Observable<any> {
      return new Observable(observer => {
          observer.error(err);
      });
    }

}
